/* eslint-disable @backmarket/custom-rules/invariant-routes */
import type { RouteLocationRaw, RouteRecordRaw } from 'vue-router'

import type { RouteExistsGuard } from '@backmarket/nuxt-layer-base/plugins/routeExists'

import { generateWebviewRoutes } from '~/scopes/webviews/generateWebviewRoutes'
import redirectIfWebview from '~/scopes/webviews/middleware/redirectIfWebview'
import { toWebviewRouteName } from '~/scopes/webviews/utils'

import { CAMPAIGN_SLUG } from './const/config.const'
import { articleBySlug } from './data/articles/data'
import { RAW_MATERIALS } from './route-names'

// Home for the active locale. Single definition of "go home" shared by the
// entry redirect and the partial-path redirect.
function homeForLocale(locale: string | string[]): RouteLocationRaw {
  return { name: 'campaign-home', params: { locale } }
}

// The article path matches any two segments, so an unknown one must 404 rather than
// render an empty reader. Matching the metal too keeps one canonical URL per article.
const articleGuard: RouteExistsGuard = (to) => {
  const article = articleBySlug(String(to.params.id))

  if (!article || article.metal.slug !== to.params.metal) {
    return { status: 404, message: 'Page Not Found' }
  }

  return true
}

const component = () => import('~/scopes/campaign/CampaignHome.vue')
const componentMeta = { key: CAMPAIGN_SLUG, middleware: [redirectIfWebview] }

// Transparent wrapper that renders only its children — the same record type the
// locale route uses one level up. Mounting every campaign route under this one
// CAMPAIGN_SLUG parent states the prefix once instead of on every child path.
const childrenWrapper = () =>
  import('@backmarket/nuxt-layer-base/components/RouteRecordWithChildrenOnly/RouteRecordWithChildrenOnly.vue')

export default (): RouteRecordRaw[] => {
  const rawMaterialsRoutes: RouteRecordRaw[] = [
    {
      path: CAMPAIGN_SLUG,
      component: childrenWrapper,
      meta: {
        layout: false,
      },
      children: [
        {
          name: RAW_MATERIALS.HOME,
          path: '',
          component,
          meta: componentMeta,
        },
        // Bare impact (no type) redirects home.
        {
          path: 'impact',
          redirect: (to) => homeForLocale(to.params.locale),
        },
        // Impact: impact/:type. Articles have their own path below, so
        // `impact/:type/:id` is gone and an old link 404s.
        {
          name: RAW_MATERIALS.IMPACT,
          path: 'impact/:type',
          component,
          meta: componentMeta,
        },
        // Metal: metal and metal/:id
        {
          name: RAW_MATERIALS.METAL,
          path: 'metal',
          component,
          meta: componentMeta,
        },
        {
          name: RAW_MATERIALS.METAL_DETAIL,
          path: 'metal/:id',
          component,
          meta: componentMeta,
        },
        // Content gallery — films and stories in one list. Cinema mode
        // is not a route: it layers via `?video=<id>` (see useModalState).
        {
          name: RAW_MATERIALS.CONTENT_GALLERY,
          path: 'content-gallery',
          component,
          meta: componentMeta,
        },
        // Superseded segment. Carries `?video=` (a name+params redirect
        // drops it) and keeps its side of the webview mirror.
        {
          path: 'video',
          redirect: (to) => ({
            name: to.meta.isWebview
              ? toWebviewRouteName(RAW_MATERIALS.CONTENT_GALLERY)
              : RAW_MATERIALS.CONTENT_GALLERY,
            params: { locale: to.params.locale },
            query: to.query,
          }),
        },
        // Take action
        {
          name: RAW_MATERIALS.TAKE_ACTIONS,
          path: 'take-action',
          component,
          meta: componentMeta,
        },
        // Article: `<metal.slug>/<article.slug>`, no impact — one story, one URL.
        // Two bare params, but vue-router ranks static segments first, so the
        // routes above still win; last here is for the reader. The param is `id`
        // because `activeDetail` reads `route.params.id` for both readers.
        {
          name: RAW_MATERIALS.ARTICLE,
          path: ':metal/:id',
          component,
          meta: {
            ...componentMeta,
            middleware: [redirectIfWebview],
            routeExists: articleGuard,
          },
        },
      ],
    },
  ]

  const webviewRawMaterialsRoutes = generateWebviewRoutes(rawMaterialsRoutes)

  return [...rawMaterialsRoutes, ...webviewRawMaterialsRoutes]
}
